import {
  ACTIVITIES_COMPONENT_TYPE_IMAGE,
  ACTIVITIES_COMPONENT_TYPE_IMAGE_SPHERE,
  ACTIVITIES_COMPONENT_TYPE_INFO_BOX,
  ACTIVITIES_COMPONENT_TYPE_VIDEO,
  ACTIVITIES_COMPONENT_TYPE_EBOOK,
  ACTIVITIES_COMPONENT_TYPE_DOCUMENT,
  ACTIVITIES_COMPONENT_TYPE_AUDIO,
  ACTIVITIES_COMPONENT_TYPE_EBOOK_INTERACTIVE,
  ACTIVITIES_COMPONENT_TYPE_INTERACTIVE,
  ACTIVITIES_COMPONENT_TYPE_LINK,
  ACTIVITIES_COMPONENT_TYPE_WEBTOOL,
  ACTIVITIES_COMPONENT_TYPE_GUIDED_RESEARCH,
  ACTIVITIES_COMPONENT_TYPE_SLIDESHOW,
  ACTIVITIES_COMPONENT_TYPE_VOCABULARY,
  ACTIVITIES_COMPONENT_TYPE_RIVE_ANIMATION,
  ACTIVITIES_COMPONENT_TYPE_GIF,
} from 'global/constants';
import {
  ComponentAudio,
  ComponentDocument,
  ComponentEbook,
  ComponentEbookInteractive,
  ComponentGuidedResearch,
  ComponentImage,
  ComponentImageSphere,
  ComponentInfoBox,
  ComponentInteractive,
  ComponentLink,
  ComponentSlideshow,
  ComponentVideo,
  ComponentVocabulary,
  ComponentRiveAnimation,
  ComponentGif,
} from 'approot/activity-builder/components/component.constants';
import type { InteractiveLessonSectionComponentFragment } from '../lesson-builder.graphql.generated';
import {
  ACTIVITY_BUILDER_EVENT_COMPONENT_EXPAND,
  ACTIVITY_BUILDER_EVENT_STIMULUS_OPEN,
  ComponentEventData,
  pushComponentViewEvent,
} from '../present/activity-builder-present-data-tracking.utils';
import InfoBoxView from './info-box/info-box-view';
import SlideshowView from './slideshow/slideshow-view';
import EbookView from './ebook/ebook-view';
import DocumentView from './document/document-view';
import VideoView from './video/video-view';
import ImageView from './image/image-view';
import ImageSphereView from './image-sphere/image-sphere-view';
import InteractiveView from './interactive/interactive-view';
import InteractiveEbookView from './interactive-ebook/interactive-ebook-view';
import LinkView from './link/link-view';
import WebToolView from './webtool/webtool-view';
import GuidedResearchView from './guided-research/guided-research-view';
import VocabularyView from './vocabulary/vocabulary-view';
import GifView from './gif/gif-view';
import RiveView from './rive/rive-view';

type Props = {
  component: InteractiveLessonSectionComponentFragment;
  eventTrackingData?: ComponentEventData;
  contentId: number;
  isStudent?: boolean;
  openModal: (component: InteractiveLessonSectionComponentFragment) => void;
};

const ComponentsViewActivityPageElements = ({
  component,
  eventTrackingData,
  contentId,
  isStudent,
  openModal,
}: Props) => {
  return (
    <>
      {component.type === ACTIVITIES_COMPONENT_TYPE_INFO_BOX && (
        <InfoBoxView
          {...(component as ComponentInfoBox)}
          openModal={() => openModal(component)}
        />
      )}

      {component.type === ACTIVITIES_COMPONENT_TYPE_SLIDESHOW && (
        <SlideshowView
          isStudent={isStudent}
          {...(component as ComponentSlideshow)}
          onComponentExpand={() =>
            pushComponentViewEvent({
              component,
              eventType: ACTIVITY_BUILDER_EVENT_COMPONENT_EXPAND,
              eventTrackingData,
              contentId,
            })
          }
        />
      )}

      {component.type === ACTIVITIES_COMPONENT_TYPE_EBOOK && (
        <EbookView
          isStudent={isStudent}
          {...(component as ComponentEbook)}
          onStimulusOpen={() =>
            pushComponentViewEvent({
              component,
              eventType: ACTIVITY_BUILDER_EVENT_STIMULUS_OPEN,
              eventTrackingData,
              contentId,
            })
          }
        />
      )}

      {component.type === ACTIVITIES_COMPONENT_TYPE_DOCUMENT && (
        <DocumentView
          isStudent={isStudent}
          {...(component as ComponentDocument)}
          onStimulusOpen={() =>
            pushComponentViewEvent({
              component,
              eventType: ACTIVITY_BUILDER_EVENT_STIMULUS_OPEN,
              eventTrackingData,
              contentId,
            })
          }
        />
      )}

      {component.type === ACTIVITIES_COMPONENT_TYPE_VIDEO && (
        <VideoView
          {...(component as ComponentVideo)}
          onStimulusOpen={() =>
            pushComponentViewEvent({
              component,
              eventType: ACTIVITY_BUILDER_EVENT_STIMULUS_OPEN,
              eventTrackingData,
              contentId,
            })
          }
        />
      )}

      {component.type === ACTIVITIES_COMPONENT_TYPE_AUDIO && (
        <VideoView
          {...(component as ComponentAudio)}
          onStimulusOpen={() =>
            pushComponentViewEvent({
              component,
              eventType: ACTIVITY_BUILDER_EVENT_STIMULUS_OPEN,
              eventTrackingData,
              contentId,
            })
          }
        />
      )}

      {component.type === ACTIVITIES_COMPONENT_TYPE_IMAGE && (
        <ImageView
          {...(component as ComponentImage)}
          openModal={() => openModal(component)}
        />
      )}

      {component.type === ACTIVITIES_COMPONENT_TYPE_IMAGE_SPHERE && (
        <ImageSphereView {...(component as ComponentImageSphere)} />
      )}

      {component.type === ACTIVITIES_COMPONENT_TYPE_INTERACTIVE && (
        <InteractiveView
          {...(component as ComponentInteractive)}
          onStimulusOpen={() =>
            pushComponentViewEvent({
              component,
              eventType: ACTIVITY_BUILDER_EVENT_STIMULUS_OPEN,
              eventTrackingData,
              contentId,
            })
          }
        />
      )}

      {component.type === ACTIVITIES_COMPONENT_TYPE_EBOOK_INTERACTIVE && (
        <InteractiveEbookView
          {...(component as ComponentEbookInteractive)}
          onStimulusOpen={() =>
            pushComponentViewEvent({
              component,
              eventType: ACTIVITY_BUILDER_EVENT_STIMULUS_OPEN,
              eventTrackingData,
              contentId,
            })
          }
        />
      )}

      {component.type === ACTIVITIES_COMPONENT_TYPE_LINK && (
        <LinkView
          {...(component as ComponentLink)}
          onStimulusOpen={() =>
            pushComponentViewEvent({
              component,
              eventType: ACTIVITY_BUILDER_EVENT_STIMULUS_OPEN,
              eventTrackingData,
              contentId,
            })
          }
        />
      )}

      {component.type === ACTIVITIES_COMPONENT_TYPE_WEBTOOL && (
        <WebToolView
          {...(component as ComponentLink)}
          onStimulusOpen={() =>
            pushComponentViewEvent({
              component,
              eventType: ACTIVITY_BUILDER_EVENT_STIMULUS_OPEN,
              eventTrackingData,
              contentId,
            })
          }
        />
      )}

      {component.type === ACTIVITIES_COMPONENT_TYPE_GUIDED_RESEARCH && (
        <GuidedResearchView
          {...(component as ComponentGuidedResearch)}
          onStimulusOpen={() =>
            pushComponentViewEvent({
              component,
              eventType: ACTIVITY_BUILDER_EVENT_STIMULUS_OPEN,
              eventTrackingData,
              contentId,
            })
          }
        />
      )}

      {component.type === ACTIVITIES_COMPONENT_TYPE_VOCABULARY && (
        <VocabularyView
          isStudent={isStudent}
          {...(component as ComponentVocabulary)}
          onComponentExpand={({ vocabularyWord, vocabularyWordCount }) =>
            pushComponentViewEvent({
              component,
              eventType: ACTIVITY_BUILDER_EVENT_COMPONENT_EXPAND,
              eventTrackingData,
              contentId,
              vocabularyWord,
              vocabularyWordCount,
            })
          }
        />
      )}

      {component.type === ACTIVITIES_COMPONENT_TYPE_RIVE_ANIMATION && (
        <RiveView {...(component as ComponentRiveAnimation)} />
      )}

      {component.type === ACTIVITIES_COMPONENT_TYPE_GIF && (
        <GifView
          {...(component as ComponentGif)}
          openModal={() => openModal(component)}
        />
      )}
    </>
  );
};

export default ComponentsViewActivityPageElements;
